import { createMemoryHistory, createRouter, createWebHistory, type Router } from 'vue-router';
import { safeSessionStorage } from '@/shared/lib/storage';
import { trackEvent } from '@/shared/lib/analytics';
import { routeList as authRouteList } from '@/app/providers/router/auth';
import { vehicleRoutes } from '@/app/providers/router/vehicles';
import { deliveryRoutes } from '@/app/providers/router/delivery-routes';
import { adminRoutes } from '@/app/providers/router/admin';
import { applicationRoutes } from '@/app/providers/router/applications';
import { errorRoutes } from '@/app/providers/router/errors';
import { authGuard } from '@/app/middleware/authGuard';

const routes = [
    ...authRouteList,
    ...errorRoutes,
    ...adminRoutes,
    ...vehicleRoutes,
    ...deliveryRoutes,
    ...applicationRoutes,
];

type CreateAppRouterOptions = {
    skipAuthGuard?: boolean;
};

export const createAppRouter = (mode: 'web' | 'memory', options: CreateAppRouterOptions = {}): Router => {
    const history = mode === 'web' ? createWebHistory() : createMemoryHistory();

    const router = createRouter({
        history,
        routes,
        scrollBehavior() {
            // ページ遷移時に常にトップにスクロール
            return { top: 0 };
        },
    });

    if (!options.skipAuthGuard) {
        router.beforeEach(authGuard);
    }

    router.afterEach((to) => {
        document.title = to.meta.title;
        if (to.meta.trackGA4) {
            trackEvent('page_view', { page_path: to.path, page_title: to.meta.title });
        }
        if (to.meta.ga4Event) {
            trackEvent(to.meta.ga4Event);
        }
    });

    // デプロイ後のチャンク読み込みエラー時にページをリロードして新しいチャンクを取得
    router.onError((error, to) => {
        const isChunkLoadError =
            error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Importing a module script failed') ||
            error.message.includes('Unable to preload CSS') ||
            error.message.includes('is not a valid JavaScript MIME type');

        if (isChunkLoadError && to) {
            const storageKey = `chunk-reload:${to.fullPath}`;
            if (!safeSessionStorage.getItem(storageKey)) {
                safeSessionStorage.setItem(storageKey, '1');
                window.location.href = to.fullPath;
            }
        }
    });

    return router;
};
